import React, { useCallback, useEffect, useState } from 'react';
import { KeyRound, CheckCircle, XCircle, Loader2, FolderOpen } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { adminGet, adminPut, adminPost, reportError, formatDate } from './adminApi';

/**
 * Dolibarr "agency" web account used only server-side so every active Easer can list the files of
 * his OWN user card (the Dolibarr REST API cannot list user files). Each Easer never sees anyone else's documents.
 */
export function AdminDolibarrAgencyCard({ adminKey }) {
  const [state, setState] = useState(null);
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await adminGet('/api/admin/dolibarr-agency', adminKey);
      setState(res.data);
      setLogin(res.data.login || '');
    } catch (err) {
      reportError('compte agence Dolibarr', err, (m) => toast.error(m || 'Chargement impossible'));
      setState({ configured: false });
    }
  }, [adminKey]);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminPut('/api/admin/dolibarr-agency', adminKey, {}, { login: login.trim(), password });
      toast.success(login.trim() ? 'Compte agence enregistré' : 'Compte agence supprimé');
      setPassword('');
      setTestResult(null);
      load();
    } catch (err) {
      reportError('compte agence Dolibarr', err, (m) => toast.error(m || 'Enregistrement impossible'));
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const res = await adminPost('/api/admin/dolibarr-agency/test', adminKey);
      setTestResult(res.data);
      if (res.data.ok) toast.success('Connexion Dolibarr réussie'); else toast.error(res.data.error || 'Connexion refusée');
      load();
    } catch (err) {
      reportError('test compte agence', err, (m) => toast.error(m || 'Test impossible'));
    } finally { setTesting(false); }
  };

  return (
    <Card className="p-5" data-testid="admin-dolibarr-agency-card">
      <div className="flex items-start gap-3 mb-3">
        <span className="h-9 w-9 rounded-lg bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] flex items-center justify-center flex-shrink-0"><FolderOpen className="h-4 w-4" /></span>
        <div>
          <h3 className="font-['Space_Grotesk'] font-semibold">Dolibarr — compte agence (documents des Easers)</h3>
          <p className="text-sm text-muted-foreground mt-0.5">L'API Dolibarr ne sait pas lister les fichiers joints d'une fiche utilisateur. Ce compte (ex. votre compte admin, droit « Lire les utilisateurs ») est utilisé uniquement par le serveur pour lire, pour chaque Easer actif connecté, <strong>ses propres</strong> documents (bulletins, contrats). Aucun Easer ne voit les documents d'un autre.</p>
        </div>
      </div>
      {state && (
        <p className="text-xs mb-3 flex items-center gap-1.5" data-testid="admin-dolibarr-agency-status">
          {state.configured ? <CheckCircle className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> : <XCircle className="h-3.5 w-3.5 text-destructive" />}
          {state.configured ? <>Configuré ({state.login}){state.last_ok ? ` · dernière connexion OK ${formatDate(state.last_ok, true)}` : ''}{state.last_error ? <span className="text-destructive"> · erreur : {state.last_error}</span> : ''}</> : 'Non configuré : seuls les Easers dont la session Dolibarr est encore active voient leurs documents.'}
        </p>
      )}
      <form onSubmit={save} className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="agency-login" className="text-xs">Identifiant Dolibarr</Label>
          <Input id="agency-login" value={login} onChange={(e) => setLogin(e.target.value)} placeholder="admin" className="h-9" autoComplete="off" data-testid="admin-dolibarr-agency-login" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="agency-password" className="text-xs">Mot de passe {state?.configured ? '(laisser vide pour conserver)' : ''}</Label>
          <Input id="agency-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="h-9" autoComplete="new-password" data-testid="admin-dolibarr-agency-password" />
        </div>
        <div className="sm:col-span-2 flex flex-wrap gap-2">
          <Button type="submit" size="sm" disabled={saving || (!state?.configured && (!login.trim() || !password))} data-testid="admin-dolibarr-agency-save">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <KeyRound className="h-4 w-4 mr-1" />} Enregistrer
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={test} disabled={testing || !state?.configured} data-testid="admin-dolibarr-agency-test">
            {testing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Tester la connexion
          </Button>
        </div>
      </form>
      {testResult && (
        <div className={`mt-3 text-xs rounded-md px-3 py-2 border ${testResult.ok ? 'bg-[hsl(var(--primary))]/10 border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))]' : 'bg-destructive/10 border-destructive/30 text-destructive'}`} data-testid="admin-dolibarr-agency-test-result">
          {testResult.ok ? <>Connexion réussie avec <strong>{testResult.login}</strong> : les Easers voient maintenant leurs documents.</> : <><strong>Échec :</strong> {testResult.error}</>}
        </div>
      )}
    </Card>
  );
}
